import React, { useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AssignmentOutlinedIcon from '@mui/icons-material/AssignmentOutlined';
import DoNotDisturbAltIcon from '@mui/icons-material/DoNotDisturbAlt';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import UndoIcon from '@mui/icons-material/Undo';
import type {
  PendingHitlInterrupt,
  PlanApprovalDraft,
  PlanGap,
  PlanGapType,
  PresentPlanArgs,
} from './types';
import { gapDismissalKey } from '@/features/hitl/planGapDismissals';
import { MarkdownContent } from '../MarkdownContent';

interface PlanApprovalCardProps {
  interrupt: PendingHitlInterrupt;
  args: PresentPlanArgs;
  draft: PlanApprovalDraft;
  onChange: (next: PlanApprovalDraft) => void;
  /**
   * Submit the plan with an explicit decision. The card calls this directly
   * to avoid the React state-update race where setting draft and submitting
   * in the same handler reads stale state. The parent (HitlInterruptPanel →
   * ChatPanel) treats this as the authoritative source for present_plan
   * submissions; the panel-level Submit button is hidden when this card is
   * the active interrupt.
   */
  onSubmitDecision: (decision: PlanApprovalDraft) => void | Promise<void>;
  isSubmitting?: boolean;
  /**
   * Whether the parent considers it valid to submit the plan now. When
   * false, the card disables every decision affordance (Approve, Submit
   * Feedback, Request Changes, Back) and short-circuits onSubmitDecision
   * — so a parent flow with later unanswered interrupts can prevent the
   * user from resuming with an incomplete HITL payload. Defaults to true
   * for the common standalone-plan case.
   */
  canSubmitPlan?: boolean;
}

const scenarioLabels: Record<string, string> = {
  'generate-report': 'Generate Report',
  'rewrite-narrative': 'Rewrite Narrative',
  'entity-updates': 'Entity Updates',
  'error-fixes': 'Error Fixes',
};

type ChipColor = 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';

const gapChipColor = (t: PlanGapType): ChipColor => {
  if (t === 'missing') return 'error';
  if (t === 'multiple-options') return 'info';
  return 'warning'; // ambiguous
};

export const PlanApprovalCard: React.FC<PlanApprovalCardProps> = ({
  args,
  draft,
  onChange,
  onSubmitDecision,
  isSubmitting = false,
  canSubmitPlan = true,
}) => {
  const [showFeedback, setShowFeedback] = useState(false);
  // Local submit flag to close the race window between click and parent
  // `isSubmitting` propagation — without this, fast repeat clicks could
  // invoke onSubmitDecision more than once for non-idempotent generation
  // flows.
  const [localSubmitting, setLocalSubmitting] = useState(false);
  const submitting = isSubmitting || localSubmitting;
  // Single source of truth for whether decision controls are interactive.
  // Parent flow constraints (canSubmitPlan=false) compose with our own
  // submitting flag.
  const actionsDisabled = submitting || !canSubmitPlan;
  const isDecided = draft.decision !== 'pending';

  // Dismissals are keyed by description because gaps carry no id — the same
  // identity the server matches on.
  const dismissedKeys = useMemo(
    () =>
      new Set(
        (draft.dismissedGaps ?? []).map((gap) => gapDismissalKey(gap.description))
      ),
    [draft.dismissedGaps]
  );

  const toggleGapDismissed = (gap: PlanGap) => {
    const key = gapDismissalKey(gap.description);
    const current = draft.dismissedGaps ?? [];
    onChange({
      ...draft,
      dismissedGaps: dismissedKeys.has(key)
        ? current.filter((entry) => gapDismissalKey(entry.description) !== key)
        : [...current, { category: gap.category, description: gap.description }],
    });
  };

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2.5,
        border: '1px solid',
        borderColor: isDecided ? 'success.light' : 'primary.light',
        borderRadius: 3,
        bgcolor: 'background.paper',
        opacity: isDecided ? 0.85 : 1,
      }}
    >
      <Stack spacing={2}>
        {/* Header */}
        <Stack direction="row" alignItems="center" spacing={1}>
          <AssignmentOutlinedIcon color="primary" fontSize="small" />
          <Typography variant="subtitle1" fontWeight={700}>
            {args.summary || 'Execution Plan'}
          </Typography>
        </Stack>

        {/* Scenario + entity type tags */}
        <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
          <Chip
            label={scenarioLabels[args.scenario] || args.scenario}
            size="small"
            color="primary"
            variant="outlined"
          />
          {args.entityTypes.map((type) => (
            <Chip key={type} label={type} size="small" variant="outlined" />
          ))}
        </Stack>

        {/* Plan briefing (markdown) */}
        <Box
          sx={{
            maxHeight: 400,
            overflow: 'auto',
            bgcolor: (theme) =>
              theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
            borderRadius: 2,
            p: 2,
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <MarkdownContent content={args.plan} />
        </Box>

        {/* Open questions (gaps) — uncertain findings the officer should
            confirm before approving. Answers flow through the Request Changes
            feedback path; each question can instead be dismissed, which the
            server records on the thread so it is never raised again. Hidden
            when the array is empty. */}
        {args.gaps && args.gaps.length > 0 ? (
          <Box>
            <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
              Open questions ({args.gaps.length - dismissedKeys.size})
            </Typography>
            <Stack spacing={1}>
              {args.gaps.map((gap: PlanGap, i) => {
                const dismissed = dismissedKeys.has(gapDismissalKey(gap.description));
                return (
                  <Box
                    key={i}
                    sx={{
                      borderLeft: 3,
                      borderColor: dismissed
                        ? 'divider'
                        : `${gapChipColor(gap.type)}.main`,
                      pl: 1.5,
                      py: 0.5,
                    }}
                  >
                    <Stack
                      direction="row"
                      spacing={1}
                      alignItems="center"
                      sx={{ mb: 0.5 }}
                    >
                      <Chip
                        size="small"
                        label={gap.type}
                        color={dismissed ? 'default' : gapChipColor(gap.type)}
                        variant={dismissed ? 'outlined' : 'filled'}
                      />
                      <Typography variant="caption" color="text.secondary">
                        {gap.category}
                      </Typography>
                      <Box sx={{ flex: 1 }} />
                      {!isDecided && (
                        <Button
                          size="small"
                          color="inherit"
                          startIcon={
                            dismissed ? (
                              <UndoIcon fontSize="small" />
                            ) : (
                              <DoNotDisturbAltIcon fontSize="small" />
                            )
                          }
                          onClick={() => toggleGapDismissed(gap)}
                          disabled={actionsDisabled}
                          aria-label={
                            dismissed
                              ? `Restore question: ${gap.description}`
                              : `Dismiss question: ${gap.description}`
                          }
                        >
                          {dismissed ? 'Undo' : "Doesn't apply"}
                        </Button>
                      )}
                    </Stack>
                    <Typography
                      variant="body2"
                      sx={
                        dismissed
                          ? { textDecoration: 'line-through', color: 'text.disabled' }
                          : undefined
                      }
                    >
                      {gap.description}
                    </Typography>
                    {dismissed ? (
                      <Typography variant="caption" color="text.secondary">
                        Dismissed — sent with your next decision, and never asked
                        again.
                      </Typography>
                    ) : gap.options && gap.options.length > 0 ? (
                      <Box component="ul" sx={{ mt: 0.5, mb: 0, pl: 3 }}>
                        {gap.options.map((opt, j) => (
                          <Typography key={j} component="li" variant="caption">
                            {opt}
                          </Typography>
                        ))}
                      </Box>
                    ) : null}
                  </Box>
                );
              })}
            </Stack>
          </Box>
        ) : null}

        {/* Technical plan (markdown, collapsible) — only rendered when present */}
        {args.technical_plan ? (
          <Accordion
            disableGutters
            elevation={0}
            defaultExpanded
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
              '&:before': { display: 'none' },
            }}
          >
            <AccordionSummary
              expandIcon={<ExpandMoreIcon />}
              sx={{ minHeight: 0, '& .MuiAccordionSummary-content': { my: 1 } }}
            >
              <Typography variant="body2" fontWeight={600}>
                Technical plan
              </Typography>
            </AccordionSummary>
            <AccordionDetails
              sx={{
                p: 2,
                maxHeight: 500,
                overflow: 'auto',
                bgcolor: (theme) =>
                  theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)',
                borderTop: '1px solid',
                borderColor: 'divider',
              }}
            >
              <MarkdownContent content={args.technical_plan} />
            </AccordionDetails>
          </Accordion>
        ) : null}

        {/* Feedback input */}
        {showFeedback && !isDecided && (
          <TextField
            fullWidth
            size="small"
            multiline
            minRows={2}
            maxRows={6}
            label="What would you like changed?"
            placeholder="e.g., wrong incident location, missing details in the summary..."
            value={draft.feedbackText}
            onChange={(e) => onChange({ ...draft, feedbackText: e.target.value })}
            autoFocus
          />
        )}

        {/* Actions */}
        {!isDecided && (
          <Stack direction="row" spacing={1} justifyContent="flex-end">
            {showFeedback ? (
              <>
                <Button
                  size="small"
                  color="inherit"
                  onClick={() => {
                    setShowFeedback(false);
                    onChange({ ...draft, feedbackText: '' });
                  }}
                  disabled={actionsDisabled}
                >
                  Back
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  disabled={!draft.feedbackText.trim() || actionsDisabled}
                  onClick={async () => {
                    // Drop the click when actions are disabled (in-flight
                    // submit OR parent says canSubmitPlan=false). Idempotency
                    // guard for the first case; flow constraint for the
                    // second.
                    if (actionsDisabled) return;
                    // Await submit BEFORE persisting decision. If the
                    // request rejects, the card stays in "pending" so the
                    // user can retry — instead of flipping to "decided" and
                    // hiding the action buttons.
                    const next: PlanApprovalDraft = { ...draft, decision: 'feedback' };
                    setLocalSubmitting(true);
                    try {
                      await onSubmitDecision(next);
                      onChange(next);
                    } catch {
                      // Submit failed — stay actionable.
                    } finally {
                      setLocalSubmitting(false);
                    }
                  }}
                >
                  {submitting ? 'Submitting…' : 'Submit Feedback'}
                </Button>
              </>
            ) : (
              <>
                <Button
                  size="small"
                  color="inherit"
                  onClick={() => setShowFeedback(true)}
                  disabled={actionsDisabled}
                >
                  Request Changes
                </Button>
                <Button
                  size="small"
                  variant="contained"
                  onClick={async () => {
                    // Same idempotency + flow-constraint + retry-on-failure
                    // contract as the Submit Feedback handler above.
                    if (actionsDisabled) return;
                    const next: PlanApprovalDraft = { ...draft, decision: 'approved' };
                    setLocalSubmitting(true);
                    try {
                      await onSubmitDecision(next);
                      onChange(next);
                    } catch {
                      // Submit failed — stay actionable.
                    } finally {
                      setLocalSubmitting(false);
                    }
                  }}
                  disabled={actionsDisabled}
                >
                  {submitting ? 'Submitting…' : 'Approve & Generate'}
                </Button>
              </>
            )}
          </Stack>
        )}

        {/* Post-decision message */}
        {isDecided && (
          <Typography variant="caption" color="text.secondary">
            {draft.decision === 'approved'
              ? 'Plan approved — generating report…'
              : 'Feedback sent — revising plan…'}
          </Typography>
        )}
      </Stack>
    </Paper>
  );
};
